import classNames from '@/common/utils/classNames';
import { ds } from '@/modules/doctorHome/designSystem';
import { ChevronLeft, type LucideIcon } from 'lucide-react';
import { ReactNode } from 'react';

/**
 * Doctor-panel design primitives — the ONLY look the five native pages use, taken from the app's doctor home
 * design system (modules/doctorHome/designSystem/tokens.ts):
 * - page #F2F2F2, white rounded-2xl sections, one level only
 * - ONE accent: primary (#3861fb). Icons: lucide, stroke 1.8, inside a bg-primary/5 text-primary circle.
 * - status colours (emerald / amber / red) only for status chips and destructive actions, never for decoration
 */

/** The unified icon: lucide glyph in a soft primary circle (same as the doctor home rows). */
export const PanelIcon = ({ icon: Icon, size = 'md', className }: { icon: LucideIcon; size?: 'sm' | 'md'; className?: string }) => (
  <span className={classNames(ds.icon.containerCircle, size === 'md' ? 'h-10 w-10' : 'h-8 w-8', className)} aria-hidden>
    <Icon className={size === 'md' ? 'h-5 w-5' : 'h-4 w-4'} strokeWidth={1.8} />
  </span>
);

/** White rounded section with an optional small primary title. */
export const PanelSection = ({ title, children, className }: { title?: string; children: ReactNode; className?: string }) => (
  <section className={classNames('overflow-hidden rounded-2xl bg-white', className)}>
    {title ? <h3 className="px-4 pb-1 pt-3.5 text-[13px] font-medium text-primary">{title}</h3> : null}
    {children}
  </section>
);

/** Row inside a section: [icon] title / subtitle … value + chevron; rows are separated by a hairline. */
export const PanelRow = ({
  icon,
  title,
  subtitle,
  value,
  onClick,
  chevron,
  end,
}: {
  icon?: LucideIcon;
  title: ReactNode;
  subtitle?: ReactNode;
  value?: ReactNode;
  onClick?: () => void;
  chevron?: boolean;
  end?: ReactNode;
}) => {
  const body = (
    <>
      {icon ? <PanelIcon icon={icon} /> : null}
      <span className="min-w-0 flex-1">
        <span className="block break-words text-[15px] font-medium leading-6 text-[#222]">{title}</span>
        {subtitle ? <span className="block break-words text-[12.5px] leading-5 text-[#767c86]">{subtitle}</span> : null}
      </span>
      {value ? <span className="shrink-0 text-[14px] text-[#767c86]">{value}</span> : null}
      {end}
      {chevron ? <ChevronLeft className="h-5 w-5 shrink-0 text-[#b0b0b0]" strokeWidth={1.8} aria-hidden /> : null}
    </>
  );
  const cls = 'flex min-h-[56px] w-full items-center gap-3 px-4 py-3 text-start transition-colors duration-150 border-t border-black/[0.06] first:border-t-0';
  return onClick ? (
    <button type="button" onClick={onClick} className={classNames(cls, 'active:bg-gray-50')}>
      {body}
    </button>
  ) : (
    <div className={cls}>{body}</div>
  );
};

/** Primary full-width action. */
export const PanelPrimaryButton = ({ children, onClick, disabled, loading }: { children: ReactNode; onClick?: () => void; disabled?: boolean; loading?: boolean }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-[15px] font-medium text-white transition-colors duration-150 active:bg-primary/90 disabled:opacity-50"
  >
    {loading ? '…' : children}
  </button>
);

/** Quiet text action in the accent colour. */
export const PanelTextButton = ({ children, onClick, tone = 'primary', icon: Icon }: { children: ReactNode; onClick?: () => void; tone?: 'primary' | 'danger'; icon?: LucideIcon }) => (
  <button
    type="button"
    onClick={onClick}
    className={classNames('flex h-11 items-center justify-center gap-1.5 rounded-xl px-3 text-[14px] font-medium transition-colors duration-150 active:bg-black/[0.04]', tone === 'danger' ? 'text-red-500' : 'text-primary')}
  >
    {Icon ? <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} aria-hidden /> : null}
    {children}
  </button>
);
